import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const HeroSection = () => {
  return (
    <section className="relative bg-gradient-to-br from-blue-50 via-cyan-50 to-blue-100 overflow-hidden py-12 sm:py-16 lg:py-20">
      {/* Animated Background */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-0 left-0 w-96 h-96 bg-blue-300 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob"></div>
        <div className="absolute top-0 right-0 w-96 h-96 bg-cyan-300 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob animation-delay-2000"></div>
        <div className="absolute bottom-0 left-1/2 w-96 h-96 bg-blue-400 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob animation-delay-4000"></div>
      </div>
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
          {/* Left Content */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
          >
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.5 }}
              className="inline-flex items-center space-x-2 px-4 py-2 bg-blue-100 rounded-full mb-6"
            >
              <Icon name="Thermometer" size={20} className="text-blue-600" />
              <span className="text-sm font-medium text-blue-700">Duurzaam verwarmen</span>
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.5 }}
              className="text-3xl sm:text-4xl lg:text-5xl font-heading font-bold text-foreground mb-4 lg:mb-6"
            >
              Warmtepomp: duurzaam verwarmen met toekomstvisie
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4, duration: 0.5 }}
              className="text-lg sm:text-xl text-muted-foreground mb-8"
            >
              Verwarm uw woning slimmer en verlaag uw gasverbruik drastisch met een moderne warmtepomp.
            </motion.p>

            {/* Key Benefits */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5, duration: 0.5 }}
              className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8"
            >
              <div className="flex items-center space-x-3 bg-white/80 backdrop-blur-sm rounded-lg p-4 shadow-elevation-1">
                <div className="w-10 h-10 bg-success/10 rounded-full flex items-center justify-center">
                  <Icon name="TrendingDown" size={20} className="text-success" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-foreground">70%</p>
                  <p className="text-xs text-muted-foreground">Energiebesparing</p>
                </div>
              </div>

              <div className="flex items-center space-x-3 bg-white/80 backdrop-blur-sm rounded-lg p-4 shadow-elevation-1">
                <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                  <Icon name="Leaf" size={20} className="text-primary" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-foreground">CO₂</p>
                  <p className="text-xs text-muted-foreground">Neutraal</p>
                </div>
              </div>

              <div className="flex items-center space-x-3 bg-white/80 backdrop-blur-sm rounded-lg p-4 shadow-elevation-1">
                <div className="w-10 h-10 bg-blue-500/10 rounded-full flex items-center justify-center">
                  <Icon name="Zap" size={20} className="text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-foreground">1:4</p>
                  <p className="text-xs text-muted-foreground">Rendement</p>
                </div>
              </div>
            </motion.div>

            {/* CTAs */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6, duration: 0.5 }}
              className="flex flex-col sm:flex-row gap-4"
            >
              <Link to="/quote-request-form">
                <Button size="lg" iconName="FileText" iconPosition="left">
                  Offerte aanvragen
                </Button>
              </Link>
              <Button size="lg" variant="outline" iconName="HelpCircle" iconPosition="left">
                Geschiktheidscheck
              </Button>
            </motion.div>
          </motion.div>

          {/* Right Image */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="relative"
          >
            <div className="relative rounded-2xl overflow-hidden shadow-elevation-3">
              <Image
                src="https://images.pexels.com/photos/8853502/pexels-photo-8853502.jpeg?auto=compress&cs=tinysrgb&w=800"
                alt="Moderne woning met discrete warmtepomp buitenunit in rustige compositie"
                className="w-full h-auto"
              />
              <div className="absolute inset-0 bg-gradient-to-br from-blue-500/20 to-cyan-500/20"></div>
            </div>

            {/* Floating Info Cards */}
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.8, duration: 0.5 }}
              className="absolute -bottom-4 -left-4 bg-white rounded-xl shadow-elevation-2 p-4 max-w-[200px]"
            >
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                  <Icon name="Award" size={24} className="text-blue-600" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-foreground">1.523 aanbieders</p>
                  <p className="text-xs text-muted-foreground">Gecertificeerd</p>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;